import { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { EvidenceViewer } from './EvidenceViewer';
import { AnnotationForm, type AnnotationMap } from './AnnotationForm';
import type { AnnotationAction } from '@/types';
import { cn } from '@/lib/utils';

export interface Finding {
  id: string;
  title: string;
  description: string;
  severity: 'BLOCKING' | 'ALERT' | 'INFO';
  sourceAgent: string;
  clauseReference: string | null;
  evidenceText: string;
  kbSource?: string | null;
  kbArticle?: string | null;
  groundingStatus: 'GROUNDED' | 'MANUAL_REVIEW';
}

interface FindingCardProps {
  finding: Finding;
  annotations: AnnotationMap;
  onAnnotate: (findingId: string, action: AnnotationAction, comment: string) => void;
  className?: string;
}

const SEVERITY_STYLE: Record<Finding['severity'], { badge: string; border: string }> = {
  BLOCKING: {
    badge: 'bg-red-100 text-red-800 border-red-300 dark:bg-red-900/30 dark:text-red-300 dark:border-red-700',
    border: 'border-l-red-500',
  },
  ALERT: {
    badge: 'bg-amber-100 text-amber-800 border-amber-300 dark:bg-amber-900/30 dark:text-amber-300 dark:border-amber-700',
    border: 'border-l-amber-500',
  },
  INFO: {
    badge: 'bg-blue-100 text-blue-800 border-blue-300 dark:bg-blue-900/30 dark:text-blue-300 dark:border-blue-700',
    border: 'border-l-blue-500',
  },
};

/**
 * Renders a single clause or compliance finding with:
 * - Severity badge, source agent, clause reference
 * - Expandable evidence viewer
 * - Inline annotation form (reviewer/admin only)
 */
export function FindingCard({ finding, annotations, onAnnotate, className }: FindingCardProps) {
  const [expanded, setExpanded] = useState(false);
  const styles = SEVERITY_STYLE[finding.severity];
  const annotation = annotations.get(finding.id);

  return (
    <Card className={cn('border-l-4', styles.border, className)}>
      <CardContent className="pt-4 pb-3 space-y-3">

        {/* Header row */}
        <div className="flex items-start gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-2 mb-1">
              <Badge
                variant="outline"
                className={cn('text-xs flex-shrink-0', styles.badge)}
              >
                {finding.severity}
              </Badge>
              <span className="text-xs text-muted-foreground flex-shrink-0">
                {finding.sourceAgent}
              </span>
              {finding.clauseReference && (
                <span className="text-xs text-muted-foreground truncate">
                  {finding.clauseReference}
                </span>
              )}
            </div>
            <p className="text-sm font-medium text-foreground">{finding.title}</p>
            <p className="text-sm text-muted-foreground mt-0.5 line-clamp-2">{finding.description}</p>
          </div>

          {/* Expand/collapse toggle */}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => { setExpanded((v) => !v); }}
            aria-expanded={expanded}
            aria-label={expanded ? 'Collapse evidence' : 'Expand evidence and annotation'}
            className="flex-shrink-0 h-8 w-8 p-0"
          >
            {expanded
              ? <ChevronUp className="h-4 w-4" aria-hidden="true" />
              : <ChevronDown className="h-4 w-4" aria-hidden="true" />
            }
          </Button>
        </div>

        {/* Expanded content */}
        {expanded && (
          <div className="space-y-3 pt-1 border-t border-border">
            <EvidenceViewer
              evidenceText={finding.evidenceText}
              contractRef={finding.clauseReference}
              kbSource={finding.kbSource}
              kbArticle={finding.kbArticle}
              groundingStatus={finding.groundingStatus}
            />
            <AnnotationForm
              findingId={finding.id}
              savedAction={annotation?.action ?? null}
              savedComment={annotation?.comment ?? null}
              onChange={onAnnotate}
            />
          </div>
        )}

        {/* Annotation stub shown when collapsed */}
        {!expanded && annotation && (
          <div className="flex items-center gap-1.5 pt-1 border-t border-border">
            <span className="text-xs text-muted-foreground">Your assessment:</span>
            <Badge
              variant="outline"
              className={cn(
                'text-xs',
                annotation.action === 'AGREE' ? 'border-green-300 text-green-700 dark:border-green-700 dark:text-green-400' :
                annotation.action === 'DISAGREE' ? 'border-red-300 text-red-700 dark:border-red-700 dark:text-red-400' :
                'border-amber-300 text-amber-700 dark:border-amber-700 dark:text-amber-400',
              )}
            >
              {annotation.action}
            </Badge>
          </div>
        )}

      </CardContent>
    </Card>
  );
}
